import React, { useMemo, useState, useEffect, useRef } from "react";
import { Ruler, ShoppingCart, Sun, Moon, Home, MapPin, Move, Maximize2, Trash2, RotateCcw } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useCart } from "../contexts/CartContext";
import { useComposition } from "../contexts/CompositionContext";
import { computeCaduceePrice, formatCHF } from "../lib/pricing";
import { snapshotConfiguratorPreview } from "../lib/snapshotPreview";
import { toast } from "sonner";
import GlobalPricePanel from "./GlobalPricePanel";
import SceneCategoryPicker from "./SceneCategoryPicker";
import MobileDesktopHint from "./MobileDesktopHint";
import { useIsMobile } from "../hooks/useIsMobile";
import CompositionOverlay from "./CompositionOverlay";
import { CROSS_SCENES } from "./CrossConfigurator";

// 11 designs de caducée extraits du SVG fourni par le client.
// Chacun pointe vers un fichier SVG individuel (silhouette utilisée en mask CSS).
// aspect = width / height (calculé sur le viewBox de chaque fichier).
// 11 modèles de caducée — mix de PNG premium (positions 1-3, 5) et de SVG
// génériques (positions 4, 6-11). L'utilisateur a explicitement demandé de
// conserver les 11 designs.
// aspect = width / height (calculé sur la taille pixel/viewBox de chaque fichier).
const DESIGNS = [
  { id: "1",  svg: "/caducee/caducee-1.png",  aspect: 3530/4545 },
  { id: "2",  svg: "/caducee/caducee-2.png",  aspect: 4036/4548 },
  { id: "3",  svg: "/caducee/caducee-3.png",  aspect: 2813/4547 },
  { id: "4",  svg: "/caducee/caducee-4.svg",  aspect: 902/1111 },
  { id: "5",  svg: "/caducee/caducee-7.png",  aspect: 2938/4594 },
  { id: "6",  svg: "/caducee/caducee-5.svg",  aspect: 990/1111 },
  { id: "7",  svg: "/caducee/caducee-6.svg",  aspect: 803/1111 },
  { id: "8",  svg: "/caducee/caducee-8.svg",  aspect: 902/1122 },
  { id: "9",  svg: "/caducee/caducee-9.svg",  aspect: 891/1122 },
  { id: "10", svg: "/caducee/caducee-10.svg", aspect: 968/1122 },
  { id: "11", svg: "/caducee/caducee-11.svg", aspect: 924/1100 },
];

// Finitions métal — réutilise les mêmes gradients CSS que les Lettres Inox 3D
// pour cohérence visuelle dans la marque Signapharma.
const FINISHES = [
  { id: "gold-brillant", label: { fr: "Gold brillant", de: "Gold glänzend", en: "Brilliant gold", it: "Oro brillante" },
    swatch: "linear-gradient(135deg,#FFF3C9 0%,#FFE08A 30%,#E0A938 60%,#8C5A0A 100%)",
    fill: "linear-gradient(180deg, #FFF3C9 0%, #FFE08A 8%, #E0A938 24%, #8C5A0A 48%, #5B3700 52%, #8C5A0A 58%, #E0A938 78%, #FFD45A 94%, #FFF3C9 100%)" },
  { id: "gold-brosse", label: { fr: "Gold brossé", de: "Gold gebürstet", en: "Brushed gold", it: "Oro spazzolato" },
    swatch: "linear-gradient(135deg,#E9C575 0%,#C99D3D 50%,#8A6112 100%)",
    fill: ["repeating-linear-gradient(90deg, rgba(255,235,180,0.18) 0px, rgba(0,0,0,0) 1px, rgba(80,50,0,0.16) 2px, rgba(0,0,0,0) 3px)",
           "linear-gradient(180deg, #E9C575 0%, #C99D3D 30%, #8A6112 55%, #B98F2E 80%, #E0BB6A 100%)"].join(", ") },
  { id: "cuivre-poli", label: { fr: "Cuivre poli", de: "Kupfer poliert", en: "Polished copper", it: "Rame lucido" },
    swatch: "linear-gradient(135deg,#FFE0C2 0%,#F2A877 30%,#C57238 60%,#6F3A12 100%)",
    fill: "linear-gradient(180deg, #FFE0C2 0%, #F2A877 8%, #C57238 24%, #6F3A12 48%, #3F1F05 52%, #6F3A12 58%, #C57238 78%, #F0A270 94%, #FFE0C2 100%)" },
  { id: "cuivre-brosse", label: { fr: "Cuivre brossé", de: "Kupfer gebürstet", en: "Brushed copper", it: "Rame spazzolato" },
    swatch: "linear-gradient(135deg,#DDA075 0%,#B47148 50%,#774524 100%)",
    fill: ["repeating-linear-gradient(90deg, rgba(255,205,160,0.18) 0px, rgba(0,0,0,0) 1px, rgba(70,30,5,0.16) 2px, rgba(0,0,0,0) 3px)",
           "linear-gradient(180deg, #DDA075 0%, #B47148 30%, #774524 55%, #A66B43 80%, #D89B73 100%)"].join(", ") },
  { id: "inox-poli", label: { fr: "Inox poli miroir", de: "Edelstahl poliert", en: "Mirror-polished stainless", it: "Inox lucido a specchio" },
    swatch: "linear-gradient(135deg,#ffffff 0%,#c8c8c8 30%,#6b6b6b 60%,#3b3b3b 100%)",
    fill: "linear-gradient(180deg, #ffffff 0%, #f6f6f6 6%, #c8c8c8 22%, #6b6b6b 48%, #3b3b3b 52%, #6b6b6b 58%, #c8c8c8 78%, #f0f0f0 94%, #ffffff 100%)" },
  { id: "inox-brosse", label: { fr: "Inox brossé", de: "Edelstahl gebürstet", en: "Brushed stainless", it: "Inox spazzolato" },
    swatch: "linear-gradient(135deg,#e4e4e4 0%,#c2c2c2 50%,#8a8a8a 100%)",
    fill: ["repeating-linear-gradient(90deg, rgba(255,255,255,0.20) 0px, rgba(255,255,255,0) 1px, rgba(0,0,0,0.12) 2px, rgba(255,255,255,0) 3px)",
           "linear-gradient(180deg, #e4e4e4 0%, #c2c2c2 30%, #8a8a8a 55%, #b0b0b0 80%, #d4d4d4 100%)"].join(", ") },
];

// Nuancier RAL (mêmes teintes que les lettres rétro-éclairées) — élargi avec
// davantage de verts (RAL 6xxx) qui sont les plus demandés par les pharmacies.
const RAL_COLORS = [
  // Neutres
  { id: "RAL9010", label: "Blanc pur",            hex: "#F1ECE2" },
  { id: "RAL9005", label: "Noir foncé",           hex: "#0A0A0A" },
  { id: "RAL7016", label: "Gris anthracite",      hex: "#293133" },
  { id: "RAL9006", label: "Alu blanc",            hex: "#A5A8A6" },
  // Rouges / oranges / jaunes
  { id: "RAL3020", label: "Rouge trafic",         hex: "#C7110F" },
  { id: "RAL3000", label: "Rouge feu",            hex: "#AB2524" },
  { id: "RAL2004", label: "Orange pur",           hex: "#E15501" },
  { id: "RAL1023", label: "Jaune trafic",         hex: "#F8AD15" },
  // Verts (palette élargie — couleur signature pharmacie)
  { id: "RAL6029", label: "Vert menthe",          hex: "#006534" },
  { id: "RAL6024", label: "Vert trafic",          hex: "#0E7C3A" },
  { id: "RAL6018", label: "Vert jaune",           hex: "#57A639" },
  { id: "RAL6017", label: "Vert mai",             hex: "#4D9C39" },
  { id: "RAL6005", label: "Vert mousse",          hex: "#114232" },
  { id: "RAL6011", label: "Vert réséda",          hex: "#587246" },
  { id: "RAL6020", label: "Vert oxyde chrome",    hex: "#374B30" },
  { id: "RAL6021", label: "Vert pâle",            hex: "#85996B" },
  { id: "RAL6027", label: "Vert clair",           hex: "#81C0BB" },
  { id: "RAL6033", label: "Vert turquoise",       hex: "#478A84" },
  // Bleus
  { id: "RAL5010", label: "Bleu gentiane",        hex: "#0E294B" },
  { id: "RAL5012", label: "Bleu clair",           hex: "#2284C6" },
  { id: "RAL5015", label: "Bleu ciel",            hex: "#1B7CB0" },
  // Pourpre / brun
  { id: "RAL4006", label: "Pourpre trafic",       hex: "#91376F" },
  { id: "RAL8011", label: "Brun noisette",        hex: "#5A3A29" },
];

// Couleurs LED de rétro-éclairage
const LED_COLORS = [
  { id: "warm3000", label: { fr: "Blanc 3000K (chaud)", de: "Weiss 3000K", en: "White 3000K", it: "Bianco 3000K" }, hex: "#FFD18A" },
  { id: "cool6500", label: { fr: "Blanc 6500K (froid)", de: "Weiss 6500K", en: "White 6500K", it: "Bianco 6500K" }, hex: "#EAF6FF" },
  { id: "blue", label: { fr: "Bleu", de: "Blau", en: "Blue", it: "Blu" }, hex: "#00B6FF" },
  { id: "green", label: { fr: "Vert", de: "Grün", en: "Green", it: "Verde" }, hex: "#22FF00" },
  { id: "red", label: { fr: "Rouge", de: "Rot", en: "Red", it: "Rosso" }, hex: "#FF1F2D" },
];

// Aspect ratio par défaut (premier design — sera surchargé par le design choisi)
// PX_PER_CM réduit pour un aperçu très proportionné à la façade réelle
// (5 / 1.5 / 1.3 ≈ 2.56 px/cm — réductions successives demandées par l'utilisateur).
const PX_PER_CM = 5 / 1.5 / 1.3;

// Toutes les scènes — exactement la même liste que les pages Croix/Lettres
// (CROSS_SCENES déjà ordonnée : angle → façades → intérieurs).
const ALL_SCENES = CROSS_SCENES;

const DEFAULT_CFG = {
  designId: "1",      // Premier modèle premium par défaut (coupe d'Hygie classique)
  finishId: "gold-brillant",
  finishType: "metal", // "metal" | "ral" — dernière finition cliquée
  ralId: "RAL9005",
  ledId: "warm3000",
  ledOn: true,
  sizeCm: 60,
  placement: "interieur", // "interieur" (par défaut) | "exterieur"
  dayNight: "night", // "day" | "night"
  // Position en % de la zone de preview (50/50 = centré). Permet de
  // déplacer librement le caducée sur l'image via drag&drop.
  posX: 50,
  posY: 50,
  // Échelle multiplicateur appliqué à la taille en px (0.4 → 2.5)
  scale: 1,
  // Par défaut : aucun caducée visible. Le client doit cliquer sur un
  // modèle dans la grille pour faire apparaître son caducée. Évite
  // d'ajouter un caducée non désiré au panier.
  visible: false,
};

const CADUCEE_STORAGE_KEY = "signapharma-caducee-cfg-v1";

export default function CaduceeConfigurator({ value, onChange }) {
  const { lang } = useI18n();
  const { addItem } = useCart();
  const { scene: globalScene, setConfigFor, reset: resetComposition } = useComposition();
  // Restaurer depuis localStorage pour persistance entre navigations sur les
  // autres pages configurateur (comme les croix/lettres).
  const [cfg, setCfg] = useState(() => {
    if (value) return { ...DEFAULT_CFG, ...value };
    try {
      const stored = JSON.parse(localStorage.getItem(CADUCEE_STORAGE_KEY) || "null");
      if (stored && typeof stored === "object") return { ...DEFAULT_CFG, ...stored };
    } catch (e) { /* ignore */ }
    return { ...DEFAULT_CFG };
  });
  const previewRef = useRef(null);
  const dragRef = useRef({ active: false, mode: "move", startY: 0, startScale: 1 });
  const [interactionMode, setInteractionMode] = useState("move");

  // Scène : synchronisée avec le CompositionContext global — exactement
  // comme les autres configurateurs (croix, lettres). Le pharmacien choisit
  // une scène une fois, elle est partagée sur TOUTES les pages.
  const isMobile = useIsMobile();
  const rawSceneId = globalScene?.sceneId || "facade-modern-white";
  // Mobile : force la scène "bardage blanc"
  const sceneId = isMobile ? "white-bardage" : rawSceneId;
  const currentScene = useMemo(
    () => ALL_SCENES.find((s) => s.id === sceneId) || ALL_SCENES[0],
    [sceneId]
  );
  const setSceneId = (id) => setConfigFor("caducee", { ...cfg, sceneId: id });

  // Synchroniser avec le parent + persister en localStorage + composition globale
  useEffect(() => {
    if (onChange) onChange(cfg);
    try {
      localStorage.setItem(CADUCEE_STORAGE_KEY, JSON.stringify(cfg));
    } catch (e) { /* quota / private mode — ignore */ }
    // Propager au CompositionContext partagé (utilisé par GlobalPricePanel
    // et par les overlays des autres pages configurateur). On inclut ralHex
    // pour que l'overlay puisse rendre la silhouette même en mode RAL.
    const ralHex = (RAL_COLORS.find((r) => r.id === cfg.ralId) || {}).hex;
    setConfigFor("caducee", { ...cfg, ralHex });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const update = (patch) => setCfg((prev) => ({ ...prev, ...patch }));

  // ---------- Drag & Drop : déplacer ou redimensionner le caducée ----------
  const startDrag = (e) => {
    if (!cfg.visible) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      active: true,
      mode: interactionMode,
      startY: (e.touches?.[0]?.clientY ?? e.clientY) || 0,
      startScale: cfg.scale || 1,
    };
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d.active || !previewRef.current) return;
    const pt = e.touches?.[0] || e;
    if (d.mode === "move") {
      const rect = previewRef.current.getBoundingClientRect();
      const x = ((pt.clientX - rect.left) / rect.width) * 100;
      const y = ((pt.clientY - rect.top) / rect.height) * 100;
      update({ posX: Math.max(5, Math.min(95, x)), posY: Math.max(5, Math.min(95, y)) });
    } else {
      const delta = d.startY - pt.clientY;
      const next = Math.max(0.4, Math.min(2.5, d.startScale + delta / 150));
      update({ scale: next });
    }
  };
  const endDrag = () => { dragRef.current.active = false; };

  useEffect(() => {
    const move = (e) => onMove(e);
    const up = () => endDrag();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactionMode, cfg.visible]);

  // ---------- Actions : recentrer / supprimer ----------
  const resetPosition = () => update({ posX: 50, posY: 50, scale: 1, visible: true });
  const removeCaducee = () => {
    update({ visible: false });
    toast.info(
      lang === "fr"
        ? "Caducée supprimé — sélectionnez un modèle pour le réafficher"
        : "Caduceus removed — pick a model to bring it back"
    );
  };

  // ---------- Raccourcis clavier : Suppr / Backspace = supprimer le caducée ----------
  // N'agit que si le caducée est visible ET si l'utilisateur n'est pas en
  // train d'éditer un champ texte (évite de bloquer les inputs/textareas).
  useEffect(() => {
    const onKey = (e) => {
      if (!cfg.visible) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removeCaducee();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.visible]);

  const currentFinish = useMemo(() => FINISHES.find((f) => f.id === cfg.finishId) || FINISHES[0], [cfg.finishId]);
  const currentLed = useMemo(() => LED_COLORS.find((l) => l.id === cfg.ledId) || LED_COLORS[0], [cfg.ledId]);
  const currentRal = useMemo(() => RAL_COLORS.find((r) => r.id === cfg.ralId) || RAL_COLORS[0], [cfg.ralId]);
  const currentDesign = useMemo(() => DESIGNS.find((d) => d.id === cfg.designId) || DESIGNS[0], [cfg.designId]);

  const materialBg = cfg.finishType === "ral" ? currentRal.hex : currentFinish.fill;
  const heightPx = cfg.sizeCm * PX_PER_CM;
  const widthPx = heightPx * currentDesign.aspect;

  const price = computeCaduceePrice(cfg.sizeCm);

  // Halo LED — drop-shadow multi-couches + halo radial pour effet rétro-éclairage
  // sur le mur (comme une vraie enseigne lumineuse).
  const ledHex = currentLed.hex;
  // En mode nuit, halo très diffus et intense ; en jour, plus subtil.
  const isNight = cfg.dayNight === "night";
  const ledFilter = cfg.ledOn
    ? (isNight
        ? `drop-shadow(0 0 6px ${ledHex}) drop-shadow(0 0 18px ${ledHex}cc) drop-shadow(0 0 45px ${ledHex}aa) drop-shadow(0 0 90px ${ledHex}88) drop-shadow(0 0 150px ${ledHex}55)`
        : `drop-shadow(0 0 4px ${ledHex}) drop-shadow(0 0 12px ${ledHex}aa) drop-shadow(0 0 28px ${ledHex}66)`)
    : "drop-shadow(0 2px 8px rgba(0,0,0,0.6))";

  // Halo "mur" — radial gradient derrière la silhouette pour simuler la lumière
  // qui se diffuse sur le mur autour de l'enseigne (effet wall-glow).
  const wallGlowSize = Math.round(widthPx * (isNight ? 2.2 : 1.6));
  const wallGlow = cfg.ledOn
    ? `radial-gradient(ellipse ${wallGlowSize}px ${wallGlowSize}px at center, ${ledHex}${isNight ? "55" : "30"} 0%, ${ledHex}${isNight ? "22" : "10"} 25%, transparent 60%)`
    : "transparent";

  const handleAddToCart = async () => {
    // Refuse l'ajout si aucun caducée n'est sélectionné (visible=false)
    if (!cfg.visible) {
      toast.error(
        lang === "fr"
          ? "Sélectionnez d'abord un modèle de caducée dans la grille."
          : lang === "de"
          ? "Bitte zuerst ein Caduceus-Modell auswählen."
          : lang === "it"
          ? "Seleziona prima un modello di caduceo."
          : "Please select a caduceus model first."
      );
      return;
    }
    const finishLabel = cfg.finishType === "ral"
      ? `RAL ${currentRal.label}`
      : (currentFinish.label[lang] || currentFinish.label.fr);
    // Capture l'aperçu visuel actuel pour l'embarquer dans la fiche PDF
    const previewImage = await snapshotConfiguratorPreview(previewRef.current);
    addItem({
      product_slug: "caducee",
      product_name: {
        fr: "Caducée pharmacie lumineux",
        de: "Beleuchteter Apotheken-Caduceus",
        en: "Illuminated pharmacy caduceus",
        it: "Caduceo farmacia luminoso",
      },
      image: "https://customer-assets.emergentagent.com/job_pharma-enseigne/artifacts/62tsgo8q_3638ed36-cb2e-43a1-a500-614908341667.png",
      unit_price_chf: price,
      configuration: {
        ...cfg,
        finishLabel,
        ledLabel: currentLed.label[lang] || currentLed.label.fr,
        priceCHF: price,
        previewImage,
      },
    });
    toast.success(
      lang === "fr"
        ? `Caducée ajouté au panier — ${formatCHF(price)}`
        : lang === "de"
        ? `Caduceus hinzugefügt — ${formatCHF(price)}`
        : lang === "it"
        ? `Caduceo aggiunto — ${formatCHF(price)}`
        : `Caduceus added to cart — ${formatCHF(price)}`
    );
  };

  // Style preview — utilise la scène (intérieure par défaut) comme arrière-plan
  const sceneBg = currentScene?.image
    ? `url('${currentScene.image}')`
    : (cfg.dayNight === "night" ? "linear-gradient(180deg, #050505 0%, #181818 100%)" : "linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)");
  // Filtre nuit : assombrit la photo de scène pour simuler l'ambiance nocturne
  const sceneFilter = cfg.dayNight === "night" ? "brightness(0.45) saturate(1.15)" : "brightness(1) saturate(1)";
  const labelCls = "font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-2";

  return (
    <div className="space-y-6" data-testid="caducee-configurator">
      <MobileDesktopHint />
      {/* Toolbar : outil de manipulation + actions (au-dessus de la preview) */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {lang === "fr" ? "Outil" : "Tool"} :
        </span>
        <button type="button" onClick={() => setInteractionMode("move")} data-testid="cad-tool-move"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${interactionMode === "move" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
          <Move size={12} />{lang === "fr" ? "Déplacer" : "Move"}
        </button>
        <button type="button" onClick={() => setInteractionMode("resize")} data-testid="cad-tool-resize"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${interactionMode === "resize" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
          <Maximize2 size={12} />{lang === "fr" ? "Redimensionner" : "Resize"}
        </button>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hidden sm:inline">
          {interactionMode === "move"
            ? (lang === "fr" ? "Glissez le caducée sur l'image" : "Drag the caduceus")
            : (lang === "fr" ? "Glissez verticalement pour ajuster la taille" : "Drag vertically to scale")}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {cfg.visible ? (
            <button type="button" onClick={removeCaducee} data-testid="cad-remove"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-500/50 text-red-400 font-mono text-[10px] uppercase tracking-widest hover:bg-red-500/10 hover:border-red-500"
              title={lang === "fr" ? "Supprimer le caducée (touche Suppr / Backspace)" : "Remove caduceus (Delete / Backspace key)"}>
              <Trash2 size={12} />{lang === "fr" ? "Supprimer" : "Remove"}
              <kbd className="ml-1 text-[8px] opacity-60 border border-red-400/40 px-1">⌫</kbd>
            </button>
          ) : null}
          <button type="button" onClick={resetPosition} data-testid="cad-reset-pos"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-zinc-400 font-mono text-[10px] uppercase tracking-widest hover:border-white/30"
            title={lang === "fr" ? "Recentrer & remettre à l'échelle" : "Recenter & reset scale"}>
            <RotateCcw size={12} />{lang === "fr" ? "Recentrer" : "Recenter"}
          </button>
          <button type="button" onClick={resetComposition} data-testid="cad-reset-composition"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-500/30 text-red-400/70 font-mono text-[10px] uppercase tracking-widest hover:bg-red-500/10"
            title={lang === "fr" ? "Vider toute la composition" : "Clear composition"}>
            <Trash2 size={12} />{lang === "fr" ? "Tout vider" : "Clear all"}
          </button>
        </div>
      </div>

      {/* Layout 2 colonnes : preview à gauche + picker scènes à droite —
          identique à CrossConfigurator et BoxLetterConfigurator. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_140px] gap-4">
        <div className="relative">
      {/* Preview */}
      <div
        ref={previewRef}
        className="relative w-full aspect-[4/3] border border-white/10 overflow-hidden select-none"
        data-testid="caducee-preview"
        style={{ cursor: cfg.visible ? (interactionMode === "move" ? "grab" : "ns-resize") : "default" }}
      >
        {/* Scène (façade ou intérieur) en arrière-plan — backgroundPosition
            "center bottom" comme sur les pages Croix/Lettres, pour bien voir
            la devanture en bas de l'image et garder les étages au-dessus. */}
        <div
          aria-hidden="true"
          data-snapshot-hide="true"
          className="absolute inset-0 transition-all duration-500"
          style={{
            backgroundImage: sceneBg,
            backgroundSize: "cover",
            backgroundPosition: "center bottom",
            filter: sceneFilter,
          }}
        />
        {/* Voile sombre supplémentaire en mode nuit */}
        {cfg.dayNight === "night" && (
          <div aria-hidden="true" data-snapshot-hide="true" className="absolute inset-0 bg-black/35 pointer-events-none" />
        )}
        {/* Overlay des éléments des AUTRES catégories (lecture seule) —
            permet de voir les croix, lettres, néons configurés sur les autres
            pages, en arrière-plan derrière le caducée. Un clic renvoie vers
            le configurateur correspondant. */}
        <div data-snapshot-hide="true">
          <CompositionOverlay currentSlug="caducee" mode={cfg.dayNight} />
        </div>
        {/* Indicateur dimensions */}
        <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest text-[#39FF14] border border-[#39FF14]/40 bg-black/60 px-2 py-1 z-10 pointer-events-none">
          {cfg.sizeCm} cm · {Math.round((cfg.scale || 1) * 100)}%
        </div>
        {/* Toggle Jour/Nuit */}
        <div className="absolute top-3 right-3 flex border border-white/15 bg-black/60 z-10">
          <button type="button" onClick={() => update({ dayNight: "day" })}
            data-testid="cad-daynight-day"
            className={`px-2 py-1 flex items-center gap-1 font-mono text-[10px] uppercase ${cfg.dayNight === "day" ? "bg-[#39FF14]/15 text-[#39FF14]" : "text-zinc-400"}`}>
            <Sun size={11} /> {lang === "fr" ? "Jour" : "Day"}
          </button>
          <button type="button" onClick={() => update({ dayNight: "night" })}
            data-testid="cad-daynight-night"
            className={`px-2 py-1 flex items-center gap-1 font-mono text-[10px] uppercase ${cfg.dayNight === "night" ? "bg-[#39FF14]/15 text-[#39FF14]" : "text-zinc-400"}`}>
            <Moon size={11} /> {lang === "fr" ? "Nuit" : "Night"}
          </button>
        </div>
        {/* Toggle LED ON/OFF */}
        <div className="absolute bottom-3 right-3 z-10">
          <button type="button" onClick={() => update({ ledOn: !cfg.ledOn })}
            data-testid="cad-led-toggle"
            className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${cfg.ledOn ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/15 text-zinc-400 bg-black/60"}`}>
            LED {cfg.ledOn ? "ON" : "OFF"}
          </button>
        </div>
        {/* Bandeau "aucun caducée sélectionné" — guide l'utilisateur vers la grille */}
        {!cfg.visible && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <div className="px-5 py-3 bg-black/85 border border-[#39FF14]/40 font-mono text-[11px] uppercase tracking-widest text-[#39FF14] text-center max-w-xs">
              {lang === "fr"
                ? "Sélectionnez un modèle de caducée ci-dessous pour le faire apparaître"
                : lang === "de"
                ? "Wählen Sie ein Caduceus-Modell unten aus"
                : lang === "it"
                ? "Seleziona un modello di caduceo qui sotto"
                : "Pick a caduceus model below to display it"}
            </div>
          </div>
        )}
        {/* Conteneur draggable centré sur posX/posY, contenant : halo + silhouette */}
        {cfg.visible && (
          <div
            className="absolute"
            style={{
              left: `${cfg.posX}%`,
              top: `${cfg.posY}%`,
              transform: "translate(-50%, -50%)",
              cursor: interactionMode === "move" ? "grab" : "ns-resize",
              touchAction: "none",
            }}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            data-testid="caducee-draggable"
          >
            {/* Halo LED projeté sur le mur (positionné sur le caducée) */}
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 pointer-events-none"
              style={{
                width: `${wallGlowSize}px`,
                height: `${wallGlowSize}px`,
                transform: "translate(-50%, -50%)",
                background: wallGlow,
                mixBlendMode: "screen",
              }}
            />
            {/* La silhouette du caducée — masque SVG + background matière */}
            <div
              aria-label="caducee preview"
              data-testid="caducee-shape"
              className="relative"
              style={{
                width: `${widthPx * (cfg.scale || 1)}px`,
                height: `${heightPx * (cfg.scale || 1)}px`,
                WebkitMaskImage: `url('${currentDesign.svg}')`,
                maskImage: `url('${currentDesign.svg}')`,
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                background: materialBg,
                filter: ledFilter,
                transition: "filter 0.4s ease, background 0.3s ease",
              }}
            />
          </div>
        )}
      </div>

      {/* Sélecteur de scène — colonne latérale droite (SceneCategoryPicker).
          Masqué sur mobile (<768px) — scène forcée à "bardage blanc". */}
        </div>
        <div className="relative lg:min-h-0 hidden md:block">
          <SceneCategoryPicker
            scenes={ALL_SCENES}
            selectedId={sceneId}
            onSelect={(id) => setSceneId(id)}
            customScene={null}
            onUploadClick={() => { /* upload custom non supporté ici */ }}
            onClearCustom={() => {}}
            thumbnailTestIdPrefix="cad-scene"
          />
        </div>
      </div>

      {/* Placement Intérieur / Extérieur */}
      <div>
        <div className={labelCls}>
          <MapPin size={12} className="text-[#39FF14]" />
          {lang === "fr" ? "Type de pose" : lang === "de" ? "Montageart" : lang === "it" ? "Tipo di posa" : "Mounting type"}
        </div>
        <div className="grid grid-cols-2 gap-2" data-testid="cad-placement">
          <button type="button" onClick={() => update({ placement: "interieur" })}
            data-testid="cad-place-interieur"
            className={`px-4 py-3 border font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2 ${cfg.placement === "interieur" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
            <Home size={14} /> {lang === "fr" ? "Intérieur" : lang === "de" ? "Innen" : lang === "it" ? "Interno" : "Indoor"}
          </button>
          <button type="button" onClick={() => update({ placement: "exterieur" })}
            data-testid="cad-place-exterieur"
            className={`px-4 py-3 border font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2 ${cfg.placement === "exterieur" ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
            <MapPin size={14} /> {lang === "fr" ? "Extérieur (IP65)" : lang === "de" ? "Aussen (IP65)" : lang === "it" ? "Esterno (IP65)" : "Outdoor (IP65)"}
          </button>
        </div>
      </div>

      {/* Sélection du design de caducée */}
      <div>
        <div className={labelCls}>
          {lang === "fr" ? "Modèle de caducée" : lang === "de" ? "Caduceus-Modell" : lang === "it" ? "Modello di caduceo" : "Caduceus model"}
          <span className="text-zinc-500">— {DESIGNS.length} {lang === "fr" ? "modèles" : "models"}</span>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-6 gap-2" data-testid="cad-design-list">
          {DESIGNS.map((d) => {
            const selected = cfg.designId === d.id && cfg.visible;
            // Le swatch de chaque tuile montre la silhouette en gold pour un aperçu
            return (
              <button key={d.id} type="button" onClick={() => update({ designId: d.id, visible: true })}
                data-testid={`cad-design-${d.id}`}
                title={`Modèle ${d.id}`}
                className={`aspect-square p-2 border bg-[#0a0a0a] flex items-center justify-center relative ${selected ? "border-[#39FF14] bg-[#39FF14]/10" : "border-white/10 hover:border-white/30"}`}>
                <div style={{
                  width: "85%",
                  height: "85%",
                  WebkitMaskImage: `url('${d.svg}')`,
                  maskImage: `url('${d.svg}')`,
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                  background: "linear-gradient(180deg, #FFE08A 0%, #E0A938 50%, #8C5A0A 100%)",
                  filter: selected ? "drop-shadow(0 0 6px #FFD45Aaa)" : "none",
                }} />
                <span className="absolute bottom-1 right-1 font-mono text-[9px] text-zinc-500">{d.id}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Finition : Métal → LED → RAL (tous affichés directement) */}
      <div>
        <div className={labelCls}>
          {lang === "fr" ? "Finition métal" : lang === "de" ? "Metallfinish" : lang === "it" ? "Finitura metallo" : "Metal finish"}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="cad-finish-metal-list">
          {FINISHES.map((f) => {
            const selected = cfg.finishType === "metal" && cfg.finishId === f.id;
            return (
              <button key={f.id} type="button" onClick={() => update({ finishId: f.id, finishType: "metal" })}
                data-testid={`cad-finish-${f.id}`}
                className={`p-2 border flex items-center gap-2 ${selected ? "border-[#39FF14] bg-[#39FF14]/10" : "border-white/10 hover:border-white/30"}`}>
                <div className="w-8 h-8 shrink-0 rounded-sm" style={{ background: f.swatch }} />
                <div className="text-left font-mono text-[10px] uppercase tracking-wider text-zinc-300">
                  {f.label[lang] || f.label.fr}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Couleur LED de rétro-éclairage — gros boutons avec label */}
      <div>
        <div className={labelCls}>
          {lang === "fr" ? "Couleur du rétro-éclairage LED" : lang === "de" ? "LED-Hinterleuchtungsfarbe" : lang === "it" ? "Colore LED di retroilluminazione" : "LED back-light colour"}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="cad-led-list">
          {LED_COLORS.map((l) => {
            const selected = cfg.ledId === l.id;
            return (
              <button key={l.id} type="button" onClick={() => update({ ledId: l.id, ledOn: true })}
                data-testid={`cad-led-${l.id}`}
                className={`px-3 py-2 border flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest ${selected ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                <span className="w-3 h-3 rounded-full" style={{ background: l.hex, boxShadow: `0 0 8px ${l.hex}` }} />
                {l.label[lang] || l.label.fr}
              </button>
            );
          })}
        </div>
      </div>

      {/* Nuancier RAL — en dessous des métaux comme alternative. Une seule
          ligne scrollable horizontalement, swatches compacts avec n° RAL. */}
      <div>
        <div className={labelCls}>
          {lang === "fr" ? "Ou peinture RAL" : lang === "de" ? "Oder RAL-Lack" : lang === "it" ? "O vernice RAL" : "Or RAL paint"}
          <span className="text-zinc-500">— {RAL_COLORS.length} {lang === "fr" ? "teintes" : "shades"}</span>
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1.5" style={{ scrollbarWidth: "thin" }} data-testid="cad-finish-ral-list">
          {RAL_COLORS.map((r) => {
            const selected = cfg.finishType === "ral" && cfg.ralId === r.id;
            const num = r.id.replace("RAL", "");
            return (
              <button key={r.id} type="button" onClick={() => update({ ralId: r.id, finishType: "ral" })}
                data-testid={`cad-ral-${r.id}`}
                title={`RAL ${num} — ${r.label}`}
                className={`shrink-0 flex flex-col items-center gap-0.5 ${selected ? "" : "opacity-90 hover:opacity-100"}`}>
                <div
                  className={`w-7 h-7 border ${selected ? "border-[#39FF14] ring-2 ring-[#39FF14]/60" : "border-white/20"}`}
                  style={{ background: r.hex }}
                />
                <span className={`font-mono text-[8px] tabular-nums ${selected ? "text-[#39FF14]" : "text-zinc-500"}`}>
                  {num}
                </span>
              </button>
            );
          })}
        </div>
        {cfg.finishType === "ral" && (
          <div className="mt-2 font-mono text-[10px] uppercase tracking-wider text-[#39FF14]">
            {lang === "fr" ? "Sélectionné" : "Selected"} : RAL {currentRal.id.replace("RAL", "")} — {currentRal.label}
          </div>
        )}
      </div>

      {/* Dimensions */}
      <div data-testid="cad-size-toolbar">
        <div className={labelCls}>
          <Ruler size={12} className="text-[#39FF14]" />
          {lang === "fr" ? "Hauteur du caducée" : lang === "de" ? "Höhe" : lang === "it" ? "Altezza" : "Height"} —{" "}
          <span className="text-[#39FF14] font-mono" data-testid="cad-dimensions">{cfg.sizeCm} cm</span>
        </div>
        <div className="flex items-center gap-3 border border-white/10 bg-[#0a0a0a] px-4 py-3">
          <span className="font-mono text-[10px] text-zinc-500">50</span>
          <input type="range" min={50} max={80} step={1} value={cfg.sizeCm}
            onChange={(e) => update({ sizeCm: parseInt(e.target.value, 10) })}
            data-testid="cad-size-slider"
            className="flex-1 accent-[#39FF14]" />
          <span className="font-mono text-[10px] text-zinc-500">80</span>
          <input type="number" min={50} max={80} step={1} value={cfg.sizeCm}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!Number.isNaN(v)) update({ sizeCm: Math.min(80, Math.max(50, v)) });
            }}
            data-testid="cad-size-input"
            className="w-20 bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-sm text-[#39FF14] text-right focus:outline-none focus:border-[#39FF14]"
          />
          <span className="font-mono text-xs text-zinc-400">cm</span>
        </div>
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {[50, 60, 70, 80].map((v) => (
            <button key={v} type="button" onClick={() => update({ sizeCm: v })}
              data-testid={`cad-size-${v}`}
              className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${cfg.sizeCm === v ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              {v} cm
            </button>
          ))}
        </div>
      </div>

      {/* Prix + Ajouter au panier */}
      <div className="p-6 bg-[#080808] border border-[#39FF14]/30">
        <div className="flex items-baseline justify-between mb-4">
          <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            {lang === "fr" ? "Prix estimé" : lang === "de" ? "Geschätzter Preis" : lang === "it" ? "Prezzo stimato" : "Estimated price"}
          </span>
          <span className="font-display text-3xl font-bold text-[#39FF14] neon-text-soft" data-testid="cad-price">
            {formatCHF(price)} <span className="text-sm font-mono">HT</span>
          </span>
        </div>
        <button type="button" onClick={handleAddToCart}
          data-testid="cad-add-to-cart"
          className="btn-neon w-full justify-center">
          <ShoppingCart size={16} />
          {lang === "fr" ? "Ajouter au panier" : lang === "de" ? "In den Warenkorb" : lang === "it" ? "Aggiungi al carrello" : "Add to cart"}
        </button>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-center">
          {lang === "fr" ? "Fabrication & livraison 3 à 5 semaines · Suisse" : lang === "de" ? "Fertigung 3–5 Wochen · Schweiz" : lang === "it" ? "Produzione 3–5 settimane · Svizzera" : "Manufacturing 3–5 weeks · Switzerland"}
        </p>
      </div>

      {/* Panneau prix global — affiche aussi les autres produits du montage */}
      <GlobalPricePanel currentSlug="caducee" localCfg={cfg} />
    </div>
  );
}
